import { Info, ExternalLink } from "lucide-react";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../ui/hover-card";

import { lookupExplainer, parseExplainerText } from "./costRowExplainers";

const _slug = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")
    .slice(0, 40);

/**
 * Cost-row label renderer. When the row's explainer carries a
 * supporting-document URL (e.g. the Translation / Transcription
 * Quality Framework PDFs) the LABEL ITSELF becomes the hyperlink -
 * clients kept missing the link when it was buried inside the ⓘ
 * hover panel. Prose-only explainers keep the ⓘ hover as before.
 */
export function CostRowLabel({ label, onLinkClick }) {
  const entry = lookupExplainer(label);
  if (!entry) return <>{label}</>;
  const { body, link } = parseExplainerText(entry.text);
  if (!link) {
    return (
      <>
        {label}
        <CostRowInfo label={label} />
      </>
    );
  }
  return (
    <>
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          // Engagement signal - "Laura opened the Transcription
          // Quality Framework". Fire-and-forget; never block the
          // navigation to the PDF.
          if (typeof onLinkClick === "function") {
            onLinkClick({ label: entry.label, row: label, url: link });
          }
        }}
        className="text-[#1B4F8A] font-medium underline decoration-[#1B4F8A]/40 underline-offset-2 hover:decoration-[#1B4F8A] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3EA8DC] rounded-sm"
        aria-label={`${label} - opens supporting document in a new tab`}
        data-testid={`cost-row-label-link-${_slug(entry.label)}`}
      >
        {label}
        <ExternalLink className="w-3 h-3 inline ml-1 -mt-0.5" aria-hidden />
      </a>
      {body && <CostRowInfo label={label} />}
    </>
  );
}

/**
 * Tiny ⓘ next to a cost-row label. Hover (desktop) or tap (mobile)
 * opens a HoverCard with the explainer. Renders nothing when the
 * label has no matching catalogue entry, the table stays clean.
 *
 * `See: <url>` lines are auto-converted to a "Read more" link inside
 * the panel; clicking the link works because HoverCard lets the
 * pointer cross into the panel without closing.
 */
export function CostRowInfo({ label }) {
  const entry = lookupExplainer(label);
  if (!entry) return null;
  const { body, link } = parseExplainerText(entry.text);
  const testid = `cost-row-info-${_slug(entry.label)}`;
  return (
    <HoverCard openDelay={120} closeDelay={120}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          aria-label={`What does ${entry.label} cover?`}
          className="inline-flex align-middle ml-1.5 -mt-0.5 text-[#1B4F8A]/60 hover:text-[#1B4F8A] focus:text-[#1B4F8A] cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3EA8DC] rounded-full"
          data-testid={testid}
        >
          <Info className="w-3.5 h-3.5" />
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="start"
        className="w-72 p-3 text-xs leading-relaxed text-stone-700 border-stone-200"
      >
        <p className="text-[10px] font-mono tracking-[0.18em] uppercase text-[#1B4F8A] mb-1.5">
          {entry.label}
        </p>
        {body && <p>{body}</p>}
        {link && (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-2 text-[#1B4F8A] font-medium hover:underline"
            data-testid={`${testid}-link`}
          >
            Read more
            <ExternalLink className="w-3 h-3" aria-hidden />
          </a>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}
